import humanizeDuration from 'humanize-duration';
import { DateTime, Duration, Interval } from 'luxon';

export const differenceInDays = (start: DateTime, end: DateTime) => {
	return Interval.fromDateTimes(start, end).length('days');
};

export const differenceInMinutes = (start: DateTime, end: DateTime) => {
	return Interval.fromDateTimes(start, end).length('minutes');
};

export const format = (date: DateTime | undefined | null, format: string) => {
	const dateToFormat = date ?? DateTime.now();
	return dateToFormat.toFormat(format);
};

export const startOfDay = (date: DateTime) => {
	return date.startOf('day');
};

export const startOfCurrentDay = () => {
	return DateTime.now().startOf('day');
};

export const endOfDay = (date: DateTime) => {
	return date.endOf('day');
};

export const endOfCurrentDay = () => {
	return DateTime.now().endOf('day');
};

export const startOfMonth = (date: DateTime) => {
	return date.startOf('month');
};

export const endOfMonth = (date: DateTime) => {
	return date.endOf('month');
};

export const startOfWeek = (date: DateTime) => {
	return date.startOf('week');
};

export const endOfWeek = (date: DateTime) => {
	return date.endOf('week');
};

export const isSameDay = (dateLeft: DateTime, dateRight: DateTime) => {
	return dateLeft.hasSame(dateRight, 'day');
};

export const isSameMonth = (dateLeft: DateTime, dateRight: DateTime) => {
	return dateLeft.hasSame(dateRight, 'month');
};

export const addDays = (date: DateTime, days: number) => {
	return date.plus({ days });
};

export const addWeeks = (date: DateTime, weeks: number) => {
	return date.plus({ weeks });
};

export const addMonths = (date: DateTime, months: number) => {
	return date.plus({ months });
};

export const getISOWeek = (date: DateTime) => {
	return date.weekNumber;
};

export const addHours = (date: DateTime, hours: number) => {
	return date.plus({ hours });
};

export const addMinutes = (date: DateTime, minutes: number) => {
	return date.plus({ minutes });
};

export const eachDayOfInterval = ({ start, end }: { start: DateTime; end: DateTime }) => {
	return Interval.fromDateTimes(start, end)
		.splitBy({ day: 1 })
		.map((i) => i.start);
};

export const fromUnixTime = (date: number) => {
	return DateTime.fromMillis(date * 1000);
};

export const isWithinInterval = (
	date: DateTime,
	{ start, end }: { start: DateTime; end: DateTime }
) => {
	return Interval.fromDateTimes(start, end).contains(date);
};

export const isToday = (date: DateTime) => {
	return date.hasSame(DateTime.now(), 'day');
};

export const isTomorrow = (date: DateTime) => {
	return date.hasSame(DateTime.now().startOf('day').plus({ days: 1 }), 'day');
};

export const isYesterday = (date: DateTime) => {
	return date.hasSame(DateTime.now().startOf('day').plus({ days: -1 }), 'day');
};

export const isAfter = (date: DateTime, compareDate: DateTime) => {
	return date > compareDate;
};

export const isBefore = (date: DateTime, compareDate: DateTime) => {
	return date < compareDate;
};

export const isLaterToday = (time: DateTime) => {
	return time.hasSame(DateTime.now(), 'day') && time > DateTime.now();
};

export const isEndOfDayToday = (time: DateTime) => {
	return time.equals(DateTime.now().endOf('day'));
};

export const isEndOfDay = (time: DateTime) => {
	return time.equals(time.endOf('day'));
};

export const isStartOfDay = (time: DateTime) => {
	return time.equals(time.startOf('day'));
};

export const isStartOfDayToday = (time: DateTime) => {
	return time.equals(DateTime.now().startOf('day'));
};

export const isStartOfDayYesterDay = (time: DateTime) => {
	return time.equals(DateTime.now().plus({ days: -1 }).startOf('day'));
};

export const isEndOfDayYesterDay = (time: DateTime) => {
	return time.equals(DateTime.now().plus({ days: -1 }).endOf('day'));
};

export const isSame = (date: DateTime, compareDate: DateTime) => {
	return date.equals(compareDate);
};

export const isSameOrAfter = (date: DateTime, compareDate: DateTime) => {
	return date.equals(compareDate) || date > compareDate;
};

export const getHours = (date: DateTime) => {
	return date.get('hour');
};

export const getMinutes = (date: DateTime) => {
	return date.get('minute');
};

export const setHours = (date: DateTime, hour: number) => {
	return date.set({ hour });
};

export const setMinutes = (date: DateTime, minute: number) => {
	return date.set({ minute });
};

export const startOfHour = (date: DateTime) => {
	return date.startOf('hour');
};

export const setSeconds = (date: DateTime, second: number) => {
	return date.set({ second });
};

export const parseISO = (date: string) => {
	return DateTime.fromISO(date);
};

export const getDate = (date: DateTime) => {
	return date.day;
};

export const toDate = (date: number) => {
	return DateTime.fromMillis(date);
};

export const formatDistanceToNow = (time: number) => {
	const duration = Interval.fromDateTimes(DateTime.fromMillis(time), DateTime.now())
		.toDuration()
		.valueOf();

	return humanizeDuration(duration);
};

export const parseFromString = (input: string, format: string) => {
	return DateTime.fromFormat(input, format);
};

export const parseFromTime = (hour: number, minute: number) => {
	return DateTime.now().set({ hour }).set({ minute }).set({ second: 0 });
};

export const parseFromISO = (input: string) => {
	return DateTime.fromISO(input);
};

export const min = (dates: DateTime[]) => {
	return DateTime.min(...dates);
};

export const max = (dates: DateTime[]) => {
	return DateTime.max(...dates);
};

export const durationFromTime = (time: string) => {
	const duration = Duration.fromISOTime(time);

	if (duration.isValid) {
		return duration;
	}

	return Duration.fromObject({ hours: 0, minutes: 0, seconds: 0 });
};
